/**
 * **Pilih printer** — dari yang AGEN lihat, bukan dari yang orang ketik.
 *
 * ### ⛔ Kenapa layar ini ada (Imron, 2026-08-23)
 * *"Ini jadi kerja dia kali. Padahal harusnya yang wajib di-setting di pos agent. Jika pos agent
 * sudah di-setting MAC-nya, harusnya di-notif ke portal untuk diisi."*
 *
 * Benar, dan alasannya lebih tajam daripada kenyamanan: **MAC adalah fakta yang mesinnya sudah
 * tahu**. Agen melihatnya di daftar ter-pair OS. Menyuruh manajer menyalin 12 digit heksadesimal
 * ke portal menghasilkan salah ketik — atau, jauh lebih sering, **tak pernah diisi**. Terbukti di
 * server live: tiga perangkat PB-PKB, nol profil printer, dan kasir menekan Cetak berhari-hari
 * sementara server dengan patuh menjawab 201.
 *
 * ### ⛔⛔ Agen TIDAK menulis ke portal
 * Ia mendengarkan `127.0.0.1` dan tak punya kredensial — dan tak boleh punya: sesuatu yang bisa
 * menulis ke portal dari loopback adalah pintu belakang untuk **setiap halaman web yang kebetulan
 * dibuka kasir** (K27, alasan yang sama dengan daftar origin). ⇒ Agen MELAPOR; layar inilah yang
 * mengadopsi, dengan token perangkat yang bisa membuktikan dirinya.
 *
 * ### ⚠️ Bluetooth saja
 * Yang bisa ditemukan sendiri hanyalah yang MENEMPEL pada mesin ini. Printer **jaringan** tak
 * menempel pada tablet mana pun — alamatnya keputusan IT dan K6 menuntut IP statis, jadi ia disetel
 * di portal (Lantai → Printer). Layar ini mengatakannya alih-alih menampilkan daftar kosong yang
 * membuat orang menyimpulkan printernya rusak.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { agentPrinters } from '../api/agent';
import type { AgentPrinter } from '../api/agent';
import { api } from '../api/client';

export interface PrinterSetupProps {
  readonly onClose: () => void;
  /** Dipanggil sesudah adopsi berhasil — pemanggil memeriksa ulang lencana printer. */
  readonly onAdopted: () => void;
}

export function PrinterSetup(p: PrinterSetupProps) {
  const { t } = useTranslation();
  const [list, setList] = useState<readonly AgentPrinter[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /** MAC yang baru saja disalin — umpan balik sesaat, bukan keadaan yang perlu diingat. */
  const [disalin, setDisalin] = useState<string | null>(null);

  const muat = useCallback(async () => {
    setList(await agentPrinters());
  }, []);

  useEffect(() => { void muat(); }, [muat]);

  /**
   * ⭐⭐ **MAC bisa DISALIN** (Imron, 2026-08-25: *"di pos agent mac/bluetooth tidak bisa di print.
   * harusnya bisa di copy mac nya"*).
   *
   * ⛔ Kenapa ini bukan kenyamanan kecil: kalau printernya ternyata tak mau dipakai dari sini
   * (belum ter-pair, atau butuh setelan yang bukan bawaan 58 mm), satu-satunya jalan lain adalah
   * mengetik 12 digit heksadesimal ke portal — di layar ponsel, dari daftar yang tak bisa disorot.
   * Itu persis cara MAC salah ketik masuk ke PB-PKB.
   *
   * ⚠️ `navigator.clipboard` menuntut konteks aman DAN bisa ditolak pengguna. Yang gagal jatuh ke
   * `execCommand` lama — dan yang gagal juga tetap MENYOROT teksnya, supaya masih bisa disalin
   * dengan tekan-lama. ⛔ Tak pernah diam.
   */
  const salin = useCallback(async (mac: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(mac);
      setDisalin(mac);
      return;
    } catch {
      /* jatuh ke jalur lama */
    }
    const ta = document.createElement('textarea');
    ta.value = mac;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      const ok = document.execCommand('copy');
      if (ok) setDisalin(mac);
    } catch {
      /* dibiarkan: penyorotan di bawah tetap memberi jalan */
    }
    ta.remove();
  }, []);

  const pakai = async (pr: AgentPrinter) => {
    setBusy(pr.mac);
    setError(null);
    try {
      /*
       * ⭐ Bawaan yang WAJAR untuk printer termal Bluetooth: 58 mm, PC437, tanpa pemotong, tanpa
       * tendangan laci. ⛔ Bukan tebakan yang disembunyikan — semuanya bisa diubah di portal, dan
       * yang salah bawaan gagal dengan cara yang terlihat (kertas terpotong salah), bukan senyap.
       */
      await api.setOwnPrinter({
        address: { transport: 'bt', mac: pr.mac, channel: 1 },
        paperWidth: 58,
        codepage: 0,
        hasCutter: false,
        hasDrawerKick: false,
        model: pr.name,
      });
      setDone(pr.name);
      p.onAdopted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="dialog printer-setup" role="dialog" data-testid="printer-setup">
      <h2>{t('cashier.printerSetup.title')}</h2>

      {error !== null && <p className="error" data-testid="printer-setup-error">{error}</p>}
      {done !== null && (
        <p className="done" data-testid="printer-setup-done">
          {t('cashier.printerSetup.done', { name: done })}
        </p>
      )}

      {list === null ? (
        <p className="empty">{t('common.loading')}</p>
      ) : list.length === 0 ? (
        /*
         * ⛔ Dua sebab yang berbeda, satu kalimat yang menyebut KEDUANYA: agen belum terpasang,
         * atau printernya belum di-pair di Setelan Android. Menebak salah satunya membuat separuh
         * orang membongkar hal yang salah.
         */
        <p className="empty" data-testid="printer-setup-empty">
          {t('cashier.printerSetup.empty')}
        </p>
      ) : (
        <ul className="printer-list">
          {list.map((pr) => (
            <li key={pr.mac} data-testid={`printer-${pr.mac}`}>
              <span className="printer-name">
                {/* ⭐ Yang MENGAKU printer ditandai — membantu memilih, tanpa memutuskan. */}
                {pr.likelyPrinter ? '★ ' : ''}{pr.name}
              </span>
              {/*
                ⭐ TOMBOL, bukan teks. Di layar ponsel teks di dalam daftar praktis tak bisa
                disorot — dan yang tak bisa disalin akan diketik ulang, dengan segala salah ketiknya.
              */}
              <button
                type="button"
                className="printer-mac"
                data-testid={`printer-mac-${pr.mac}`}
                title={t('cashier.printerSetup.copy')}
                onClick={() => void salin(pr.mac)}
              >
                {pr.mac}
                {disalin === pr.mac && (
                  <span className="ok" data-testid={`printer-mac-copied-${pr.mac}`}>
                    {' '}{t('cashier.printerSetup.copied')}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="action-primary"
                data-testid={`printer-use-${pr.mac}`}
                disabled={busy !== null}
                onClick={() => void pakai(pr)}
              >
                {busy === pr.mac ? t('common.loading') : t('cashier.printerSetup.use')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ⚠️ Printer JARINGAN & USB tak ditemukan dari sini — dikatakan, bukan didiamkan. */}
      <p className="hint" data-testid="printer-setup-lan">{t('cashier.printerSetup.lanHint')}</p>

      <div className="dialog-actions">
        <button type="button" data-testid="printer-setup-refresh" onClick={() => void muat()}>
          {t('cashier.printerSetup.refresh')}
        </button>
        <button type="button" className="action-primary" data-testid="printer-setup-close"
                onClick={p.onClose}>
          {t('cashier.printerSetup.close')}
        </button>
      </div>
    </div>
  );
}
